import { HeaderBidder } from '@tunein/web-common';
import WebMediaPlayer from '@tunein/web-media-player';
import { pageLifeCycleWatcher, wait } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { forEach } from 'es-toolkit/compat';
import { v4 as uuidv4 } from 'uuid';
import { providerToAdNetworkMap } from '../AdPlayer/AdReporter/constants';
import { APS_BIDS_RESPONSE_LOGGING_ENABLED, clientAdTypes, FREESTAR_ENABLED, } from '../AdPlayer/constants';
import { appSessionDataProvider } from '../providers';
import { api } from '../singletons';
import { buildCommonCustomParams, buildGoogleIMAAdTagUrl } from '../utils/adTagBuilder';
import { AD_REQUEST_RETRY_DELAY_SECONDS, DEFAULT_OUTSTREAM_REQUEST_CONTEXT_URL, DEFAULT_OUTSTREAM_VOLUME, OUTSTREAM_LOOP_STALL_THRESHOLD_MS, } from './constants';
import { OutstreamVideoAdReporter } from './OutstreamVideoAdReporter';
import { createOutstreamVideoAdHouseAdsMediaElementId, createOutstreamVideoAdMediaElementId, debug, isJavaScriptAdEvent, } from './utils';
export class OutstreamVideoAdPlayer {
    #player;
    #houseAdsPlayer;
    #playerManager;
    #pendingPlayerManager;
    #activeAdRequestId;
    #sessionConfig;
    #containerId;
    #mediaElementId;
    #houseAdsMediaElementId;
    #houseAdsSources;
    #nextHouseAdIndex = 0;
    #reporter = new OutstreamVideoAdReporter();
    #volume = DEFAULT_OUTSTREAM_VOLUME;
    #muted = true;
    #didVideoAdPlay = false;
    #isFirstInSession = true;
    #isBlockedByMainPlayerVideoAd = false;
    #isDestroyed = false;
    #didCurrentAdBreakComplete = false;
    #loadedAdEvent;
    #didCurrentAdStart = false;
    #startPromise;
    #startPromiseCreatedAt;
    #readyPromise = Promise.withResolvers();
    #isInitializing = false;
    #lifecycleVersion = 0;
    #mainPlayerEventsRegistered = false;
    #controls;
    #showControls;
    #loopAbortController;
    #houseAdsAbortController;
    #slotLayoutObserver;
    #slotSizeObserver;
    #mainPlayerBlockResolvers = new Set();
    #playerEvents;
    #hiddenAt;
    #headerBidder;
    #isPlayingHouseAds = false;
    // ── Constructor & public API ──────────────────────────────────────────
    constructor(options) {
        api.init(options.api);
        this.#containerId = options.id;
        this.#mediaElementId = createOutstreamVideoAdMediaElementId(this.#containerId);
        this.#houseAdsMediaElementId = createOutstreamVideoAdHouseAdsMediaElementId(this.#containerId);
        this.#houseAdsSources = api.getConfigSetting('ads.outstreamVideoAd.houseAds', []);
        this.#pendingPlayerManager = options.playerManager;
        this.#showControls = options.controls !== false;
        this.#playerEvents = {
            adclick: this.#onAdClick,
            aderror: this.#onAdError,
            adloaded: this.#onAdLoaded,
            adpause: this.#onAdPause,
            adresume: this.#onAdResume,
            adstart: this.#onAdStart,
            adcomplete: this.#onAdComplete,
            adskipped: this.#onAdSkipped,
            alladscomplete: this.#onAllAdsComplete,
        };
        this.#headerBidder = new HeaderBidder(api.getPrebiddingConfig(clientAdTypes.outstream));
        appSessionDataProvider.on('consentDataUpdated', this.#onConsentDataUpdated);
        debug('Creating outstream video ad player', {
            containerId: this.#containerId,
            hasHouseAds: this.#houseAdsSources.length > 0,
            houseAdsCount: this.#houseAdsSources.length,
            houseAdsMediaElementId: this.#houseAdsMediaElementId,
            mediaElementId: this.#mediaElementId,
        });
        pageLifeCycleWatcher.init();
        pageLifeCycleWatcher.on('change', this.#onPageVisibilityChange);
        this.#init();
    }
    get ready() {
        return this.#readyPromise.promise;
    }
    get muted() {
        return this.#muted;
    }
    async play() {
        if (!this.#player && !this.#isInitializing) {
            debug('Skipping outstream play because the player is not initialized');
            return false;
        }
        await this.ready;
        if (!this.#player?.isAdBreak()) {
            debug('Skipping outstream play because there is no paused ad break to resume');
            return false;
        }
        if (this.#isBlockedByMainPlayerVideoAd) {
            debug('Skipping outstream play because the main player is in a video ad break');
            return false;
        }
        debug('Resuming outstream ad playback');
        this.#showOutstreamContainer();
        this.#applyVolume();
        await this.#player.play();
        return true;
    }
    pause() {
        debug('Pausing outstream ad playback');
        this.#player?.pause();
        this.#houseAdsPlayer?.pause();
    }
    mute(value) {
        this.#muted = value;
        this.#applyVolume();
    }
    setVolume(value) {
        const nextVolume = Math.max(0, Math.min(1, value));
        this.#volume = nextVolume;
        this.#muted = nextVolume === 0;
        this.#applyVolume();
    }
    destroy() {
        if (this.#isDestroyed) {
            debug.verbose('Skipping destroy because the outstream player is already destroyed');
            return;
        }
        debug('Destroying outstream video ad player', {
            containerId: this.#containerId,
            mediaElementId: this.#mediaElementId,
        });
        this.#isDestroyed = true;
        this.#headerBidder.stopVideoBidsPolling();
        this.#hiddenAt = undefined;
        pageLifeCycleWatcher.off('change', this.#onPageVisibilityChange);
        appSessionDataProvider.off('consentDataUpdated', this.#onConsentDataUpdated);
        this.#teardown();
    }
    #onConsentDataUpdated = async (consentData) => {
        await this.#headerBidder.ready;
        this.#headerBidder.updateConsent(consentData);
    };
    // ── Lifecycle ─────────────────────────────────────────────────────────
    async #init() {
        if (this.#isDestroyed) {
            debug('Skipping init because the outstream player has already been destroyed');
            return;
        }
        if (this.#isInitializing) {
            debug('Outstream init already in progress; waiting for readiness');
            await this.ready;
            return;
        }
        if (this.#player) {
            debug('Skipping init because the outstream player is already initialized');
            return;
        }
        const lifecycleVersion = this.#lifecycleVersion;
        const readyPromise = this.#readyPromise;
        this.#isInitializing = true;
        debug('Initializing outstream video ad player', {
            containerId: this.#containerId,
            mediaElementId: this.#mediaElementId,
            lifecycleVersion,
        });
        try {
            await this.#updateOutstreamVideoAdConfig();
            await appSessionDataProvider.ready;
            // Fire-and-forget: sub-bidders self-gate their fetch path on init
            // readiness, so the outstream loop can start rendering without waiting
            // for the APS/Magnite iframe handshakes to complete.
            this.#initializeHeaderBidder();
            if (lifecycleVersion !== this.#lifecycleVersion) {
                debug('Outstream init was superseded before completion; cancelling setup', {
                    lifecycleVersion,
                    currentLifecycleVersion: this.#lifecycleVersion,
                });
                readyPromise.resolve();
                return;
            }
            const outstreamPlayer = new WebMediaPlayer({
                advertising: appSessionDataProvider.outstreamVideoAdOptions,
                storage: {
                    enabled: false,
                },
            });
            outstreamPlayer.init();
            this.#player = outstreamPlayer;
            this.#ensureHouseAdsPlayer();
            this.#applyVolume();
            this.#registerPlayerEvents();
            this.#mountControls();
            this.#ensureSlotLayout();
            debug('Initialized isolated outstream Web Media Player');
            if (this.#isDestroyed || lifecycleVersion !== this.#lifecycleVersion) {
                readyPromise.resolve();
                return;
            }
            readyPromise.resolve();
            debug('Outstream player ready; starting outstream ad loop');
            this.#startLoop(lifecycleVersion);
            // Register main-player events once the manager signals readiness.
            // This is intentionally non-blocking for the outstream ad loop.
            await this.#pendingPlayerManager.ready;
            if (!this.#isDestroyed && lifecycleVersion === this.#lifecycleVersion) {
                this.#playerManager = this.#pendingPlayerManager;
                this.#registerMainPlayerEvents();
            }
        }
        catch (error) {
            debug.warn('Outstream player init failed; destroying instance', error);
            if (!this.#isDestroyed) {
                this.destroy();
            }
        }
        finally {
            if (this.#readyPromise === readyPromise) {
                this.#isInitializing = false;
            }
        }
    }
    #teardown() {
        const readyPromise = this.#readyPromise;
        this.#lifecycleVersion += 1;
        this.#loopAbortController?.abort();
        this.#houseAdsAbortController?.abort();
        this.#deregisterPlayerEvents();
        this.#deregisterMainPlayerEvents();
        this.#controls?.destroy();
        this.#controls = undefined;
        this.#disconnectSlotLayoutObserver();
        this.#disconnectSlotSizeObserver();
        this.#showOutstreamContainer();
        this.#stopHouseAds({ abortLoop: false, reason: 'teardown' });
        this.#stop();
        this.#houseAdsPlayer = undefined;
        this.#removeHouseAdsMediaElement();
        this.#player = undefined;
        this.#isInitializing = false;
        this.#sessionConfig = undefined;
        this.#activeAdRequestId = undefined;
        this.#isBlockedByMainPlayerVideoAd = false;
        this.#resolveMainPlayerBlockWaiters();
        this.#isPlayingHouseAds = false;
        this.#startPromise = undefined;
        this.#resetSessionState();
        readyPromise.resolve();
    }
    #restart() {
        if (this.#isDestroyed) {
            return;
        }
        debug('Restarting outstream video ad player');
        this.#teardown();
        this.#readyPromise = Promise.withResolvers();
        this.#init();
    }
    async #startLoop(lifecycleVersion) {
        this.#loopAbortController = new AbortController();
        let isFirstRequest = true;
        while (!this.#isDestroyed && this.#lifecycleVersion === lifecycleVersion) {
            if (!pageLifeCycleWatcher.isVisible) {
                debug('Page is hidden; waiting for visibility before next outstream ad request');
                await this.#waitForVisibility(this.#loopAbortController?.signal);
                continue;
            }
            if (this.#isDestroyed || this.#lifecycleVersion !== lifecycleVersion) {
                break;
            }
            if (this.#isBlockedByMainPlayerVideoAd) {
                debug('Main-player video ad in progress; waiting for it to complete');
                await this.#waitForMainPlayerAdComplete(this.#loopAbortController?.signal);
                continue;
            }
            if (this.#isDestroyed || this.#lifecycleVersion !== lifecycleVersion) {
                break;
            }
            if (!isFirstRequest) {
                debug('Requesting next outstream ad set');
            }
            const startCalledAt = Date.now();
            const didPlay = await this.#start().catch((error) => {
                debug.warn('Outstream ad loop iteration failed', error);
                this.#reportLoopIterationError(error);
                return false;
            });
            if (Date.now() - startCalledAt > OUTSTREAM_LOOP_STALL_THRESHOLD_MS) {
                this.#reportLoopStall(Date.now() - startCalledAt, 'start');
            }
            if (this.#isDestroyed || this.#lifecycleVersion !== lifecycleVersion) {
                break;
            }
            if (!didPlay) {
                const retryDelaySeconds = api.getConfigSetting('ads.outstreamVideoAd.refreshAfterFallbackSeconds', AD_REQUEST_RETRY_DELAY_SECONDS);
                debug(`Outstream ad request failed; retrying in ${retryDelaySeconds} seconds`);
                const delayStart = Date.now();
                await this.#waitForRetryDelay(retryDelaySeconds, lifecycleVersion).catch(noop);
                if (Date.now() - delayStart > OUTSTREAM_LOOP_STALL_THRESHOLD_MS) {
                    this.#reportLoopStall(Date.now() - delayStart, 'waitForRetryDelay');
                }
            }
            isFirstRequest = false;
        }
        debug('Outstream ad loop ended', {
            isDestroyed: this.#isDestroyed,
            lifecycleVersion,
            currentLifecycleVersion: this.#lifecycleVersion,
        });
    }
    async #start() {
        if (this.#startPromise) {
            const pendingMs = Date.now() - (this.#startPromiseCreatedAt ?? Date.now());
            if (pendingMs > OUTSTREAM_LOOP_STALL_THRESHOLD_MS) {
                this.#reportLoopStall(pendingMs, 'startPromiseReused');
            }
            return this.#startPromise;
        }
        const startPromise = this.#runStart();
        this.#startPromise = startPromise;
        this.#startPromiseCreatedAt = Date.now();
        try {
            return await startPromise;
        }
        finally {
            if (this.#startPromise === startPromise) {
                this.#startPromise = undefined;
                this.#startPromiseCreatedAt = undefined;
            }
        }
    }
    async #runStart() {
        if (!this.#player) {
            debug('Skipping outstream start because the isolated player is not initialized');
            return false;
        }
        this.#syncSessionConfig();
        const adUnitConfig = api.getAdUnitConfig(clientAdTypes.outstream, 'video');
        const isPlatformEligible = !api.isPremium && appSessionDataProvider.hasAdConfig;
        const isEligible = Boolean(isPlatformEligible && !this.#isBlockedByMainPlayerVideoAd && adUnitConfig?.adUnitId);
        const skipReasons = [
            !appSessionDataProvider.hasAdConfig && 'missingProviderConfig',
            api.isPremium && 'premiumUser',
            this.#isBlockedByMainPlayerVideoAd && 'blockedByMainPlayerVideoAd',
            !adUnitConfig && 'missingOutstreamAdUnitConfig',
            Boolean(adUnitConfig && !adUnitConfig.adUnitId) && 'missingOutstreamAdUnitId',
        ].filter(Boolean);
        const eligibilityContext = {
            hasSessionGuideId: Boolean(this.#sessionConfig?.guideId),
            adUnitConfig,
            hasAdConfig: appSessionDataProvider.hasAdConfig,
            isBlockedByMainPlayerVideoAd: this.#isBlockedByMainPlayerVideoAd,
            isPlatformEligible,
            isPremium: api.isPremium,
            skipReasons,
        };
        debug('Outstream ad eligibility evaluated', {
            ...eligibilityContext,
            isEligible,
        });
        this.#reporter.reportEligibility({
            isPlatformEligible,
            isEligible,
        });
        if (!isEligible || !adUnitConfig) {
            this.#headerBidder.stopVideoBidsPolling();
            debug('Skipping outstream ad request because eligibility failed', eligibilityContext);
            this.#stop();
            return false;
        }
        const adRequestId = uuidv4();
        const videoApsBids = this.#headerBidder.consumeVideoBidsAdParams();
        const adRequestUrl = this.#generateAdTagUrl(adUnitConfig.adUnitId, videoApsBids);
        debug('Requesting outstream ad break', {
            adRequestId,
            adUnitId: adUnitConfig.adUnitId,
            provider: adUnitConfig.provider,
        });
        this.#player.setOptions({
            advertising: appSessionDataProvider.outstreamVideoAdOptions,
        });
        this.#applyVolume();
        this.#reporter.sessionMetadata = this.#getSessionMetadata(adUnitConfig.adUnitId, adUnitConfig.provider, adRequestId, adRequestUrl, videoApsBids?.bidRequests?.map(({ requestId, network, hasBid }) => ({
            requestId,
            networkName: network,
            isIncluded: hasBid,
        })));
        this.#activeAdRequestId = adRequestId;
        this.#didCurrentAdBreakComplete = false;
        this.#loadedAdEvent = undefined;
        this.#didCurrentAdStart = false;
        this.#isFirstInSession = false;
        try {
            this.#reporter.reportAdRequested();
            const adBreakPromise = this.#player.requestAdBreak(adUnitConfig.provider, adRequestId, adRequestUrl);
            if (api.getPrebiddingConfig(clientAdTypes.outstream)) {
                // Use the ready snapshot for the current ad request, then warm the
                // next provider snapshots in the background without blocking playback.
                this.#headerBidder.pollAndCacheVideoBids();
            }
            const playedAdsCount = await adBreakPromise;
            if (!playedAdsCount) {
                if (this.#loadedAdEvent && !this.#didCurrentAdStart) {
                    this.#reportPlaybackNotStarted(adRequestId, adUnitConfig);
                    return false;
                }
                debug('Outstream ad request returned no fill', {
                    adRequestId,
                    adUnitId: adUnitConfig.adUnitId,
                });
                this.#reporter.reportNoFill(adRequestId);
                return false;
            }
            if (!this.#didCurrentAdBreakComplete) {
                debug('Outstream ad break ended before completion', {
                    adRequestId,
                    playedAdsCount,
                });
                this.#reporter.reportAdBreakInterrupted();
            }
            this.#didVideoAdPlay = true;
            debug('Outstream ad request produced playback', {
                adRequestId,
                playedAdsCount,
            });
            return true;
        }
        catch (error) {
            debug.warn('Outstream ad request failed', error);
            if (!error.isAdError) {
                this.#reporter.reportError(error, { requestId: adRequestId });
            }
            return false;
        }
    }
    #stop() {
        debug.verbose('Stopping outstream ad playback');
        this.#player?.stop();
    }
    // ── House ads ────────────────────────────────────────────────────────
    #ensureHouseAdsPlayer() {
        if (!this.#houseAdsSources.length) {
            debug('Skipping outstream house ads player because no house ads are configured', {
                configSetting: 'ads.outstreamVideoAd.houseAds',
            });
            return;
        }
        if (this.#houseAdsPlayer) {
            debug.verbose('Reusing outstream house ads player', {
                mediaElementId: this.#houseAdsMediaElementId,
            });
            return this.#houseAdsPlayer;
        }
        const houseAdsMediaElement = this.#ensureHouseAdsMediaElement();
        if (!houseAdsMediaElement) {
            debug('Skipping outstream house ads player because the media element could not be created', {
                containerId: this.#containerId,
                mediaElementId: this.#houseAdsMediaElementId,
            });
            return;
        }
        const houseAdsPlayer = new WebMediaPlayer({
            mediaElementId: this.#houseAdsMediaElementId,
            storage: {
                enabled: false,
            },
        });
        houseAdsPlayer.init();
        this.#houseAdsPlayer = houseAdsPlayer;
        this.#applyVolume();
        debug('Initialized isolated outstream house ads Web Media Player', {
            containerId: this.#containerId,
            houseAdsCount: this.#houseAdsSources.length,
            mediaElementId: this.#houseAdsMediaElementId,
        });
        return houseAdsPlayer;
    }
    #getNextHouseAdSource() {
        if (!this.#houseAdsSources.length) {
            return;
        }
        const nextHouseAd = this.#houseAdsSources[this.#nextHouseAdIndex];
        this.#nextHouseAdIndex = (this.#nextHouseAdIndex + 1) % this.#houseAdsSources.length;
        return nextHouseAd;
    }
    #getHouseAdsMediaElement() {
        return (this.#houseAdsPlayer?.getMediaElement() ??
            document.getElementById(this.#houseAdsMediaElementId));
    }
    #ensureHouseAdsMediaElement() {
        const containerElement = document.getElementById(this.#containerId);
        if (!containerElement) {
            debug('Skipping outstream house ads media element creation because the container was not found');
            return;
        }
        let mediaElement = this.#getHouseAdsMediaElement();
        if (!mediaElement) {
            mediaElement = document.createElement('video');
            debug.verbose('Creating outstream house ads media element', {
                containerId: this.#containerId,
                mediaElementId: this.#houseAdsMediaElementId,
            });
            mediaElement.id = this.#houseAdsMediaElementId;
            mediaElement.autoplay = false;
            mediaElement.controls = false;
            mediaElement.preload = 'auto';
            mediaElement.setAttribute('playsinline', '');
            containerElement.appendChild(mediaElement);
        }
        mediaElement.style.display = 'none';
        if (!containerElement.contains(mediaElement)) {
            containerElement.appendChild(mediaElement);
        }
        return mediaElement;
    }
    #hideHouseAdsMediaElement() {
        const mediaElement = this.#getHouseAdsMediaElement();
        if (!mediaElement) {
            return;
        }
        mediaElement.style.display = 'none';
    }
    #showHouseAdsMediaElement() {
        const mediaElement = this.#ensureHouseAdsMediaElement();
        if (!mediaElement) {
            debug('Skipping outstream house ads media element reveal because the media element is unavailable', {
                mediaElementId: this.#houseAdsMediaElementId,
            });
            return;
        }
        this.#ensureSlotLayout();
        mediaElement.style.display = 'block';
    }
    #removeHouseAdsMediaElement() {
        this.#getHouseAdsMediaElement()?.remove();
    }
    #stopHouseAds({ abortLoop = true, reason = 'unknown', } = {}) {
        const hasHouseAdsArtifacts = Boolean(this.#houseAdsPlayer || this.#getHouseAdsMediaElement() || this.#isPlayingHouseAds);
        if (!hasHouseAdsArtifacts) {
            debug.verbose('Skipping outstream house ads stop because nothing is active', {
                reason,
            });
            return;
        }
        debug('Stopping outstream house ads', {
            abortLoop,
            isPlayingHouseAds: this.#isPlayingHouseAds,
            reason,
        });
        if (abortLoop) {
            this.#houseAdsAbortController?.abort();
            this.#houseAdsAbortController = undefined;
            this.#isPlayingHouseAds = false;
        }
        this.#houseAdsPlayer?.stop();
        this.#hideHouseAdsMediaElement();
    }
    #startHouseAdsLoop(lifecycleVersion) {
        if (!this.#houseAdsSources.length) {
            debug('Skipping outstream house ads during retry because no house ads are configured', {
                configSetting: 'ads.outstreamVideoAd.houseAds',
            });
            return;
        }
        const houseAdsPlayer = this.#ensureHouseAdsPlayer();
        if (!houseAdsPlayer) {
            debug('Skipping outstream house ads during retry because the player is unavailable', {
                containerId: this.#containerId,
                mediaElementId: this.#houseAdsMediaElementId,
            });
            return;
        }
        if (this.#isPlayingHouseAds) {
            debug.verbose('Keeping outstream house ads running while awaiting outstream fill', {
                lifecycleVersion,
            });
            return;
        }
        this.#houseAdsAbortController?.abort();
        const abortController = new AbortController();
        this.#houseAdsAbortController = abortController;
        this.#isPlayingHouseAds = true;
        debug('Starting outstream house ads loop while awaiting outstream fill', {
            houseAdsCount: this.#houseAdsSources.length,
            lifecycleVersion,
        });
        this.#playHouseAdsLoop(houseAdsPlayer, lifecycleVersion, abortController);
    }
    async #playHouseAdsLoop(houseAdsPlayer, lifecycleVersion, abortController) {
        try {
            while (!this.#isDestroyed &&
                this.#lifecycleVersion === lifecycleVersion &&
                !abortController.signal.aborted) {
                if (!pageLifeCycleWatcher.isVisible) {
                    debug('Waiting to resume outstream house ads until the page is visible');
                    await this.#waitForVisibility(abortController.signal);
                    continue;
                }
                if (this.#isBlockedByMainPlayerVideoAd) {
                    debug('Waiting to resume outstream house ads until the main-player video ad completes');
                    await this.#waitForMainPlayerAdComplete(abortController.signal);
                    continue;
                }
                const houseAdIndex = this.#nextHouseAdIndex;
                const houseAdSource = this.#getNextHouseAdSource();
                if (!houseAdSource) {
                    debug('Stopping outstream house ads because there is no next house ad URL', {
                        houseAdsCount: this.#houseAdsSources.length,
                    });
                    return;
                }
                this.#showHouseAdsMediaElement();
                this.#applyVolume();
                debug('Loading outstream house ad', {
                    houseAdIndex,
                    houseAdUrl: houseAdSource,
                    mediaElementId: this.#houseAdsMediaElementId,
                });
                try {
                    await houseAdsPlayer.load(houseAdSource);
                    debug('Loaded outstream house ad', {
                        houseAdIndex,
                        houseAdUrl: houseAdSource,
                    });
                    if (this.#isDestroyed ||
                        this.#lifecycleVersion !== lifecycleVersion ||
                        abortController.signal.aborted) {
                        debug('Stopping loaded outstream house ad before playback because the lifecycle changed', {
                            houseAdIndex,
                            houseAdUrl: houseAdSource,
                            isDestroyed: this.#isDestroyed,
                            lifecycleVersion,
                            currentLifecycleVersion: this.#lifecycleVersion,
                            isAborted: abortController.signal.aborted,
                        });
                        houseAdsPlayer.stop();
                        return;
                    }
                    if (!pageLifeCycleWatcher.isVisible || this.#isBlockedByMainPlayerVideoAd) {
                        debug('Stopping loaded outstream house ad before playback because playback is no longer allowed', {
                            houseAdIndex,
                            houseAdUrl: houseAdSource,
                            isBlockedByMainPlayerVideoAd: this.#isBlockedByMainPlayerVideoAd,
                            isVisible: pageLifeCycleWatcher.isVisible,
                        });
                        houseAdsPlayer.stop();
                        continue;
                    }
                    debug('Starting outstream house ad playback', {
                        houseAdIndex,
                        houseAdUrl: houseAdSource,
                    });
                    await houseAdsPlayer.play();
                    debug('Outstream house ad playback started', {
                        houseAdIndex,
                        houseAdUrl: houseAdSource,
                    });
                }
                catch (error) {
                    if (this.#isDestroyed ||
                        this.#lifecycleVersion !== lifecycleVersion ||
                        abortController.signal.aborted) {
                        return;
                    }
                    debug.warn('Outstream house ad failed to start', error);
                    houseAdsPlayer.stop();
                    continue;
                }
                const playbackResult = await this.#waitForHouseAdPlaybackResult(abortController.signal);
                if (playbackResult === 'aborted' ||
                    this.#isDestroyed ||
                    this.#lifecycleVersion !== lifecycleVersion) {
                    debug('Stopping outstream house ads after playback result', {
                        houseAdIndex,
                        houseAdUrl: houseAdSource,
                        playbackResult,
                        isDestroyed: this.#isDestroyed,
                        lifecycleVersion,
                        currentLifecycleVersion: this.#lifecycleVersion,
                    });
                    return;
                }
                if (playbackResult === 'stopped') {
                    debug('Outstream house ad playback was interrupted before completion', {
                        houseAdIndex,
                        houseAdUrl: houseAdSource,
                    });
                    continue;
                }
                debug('Outstream house ad playback completed', {
                    houseAdIndex,
                    houseAdUrl: houseAdSource,
                    playbackResult,
                });
                houseAdsPlayer.stop();
            }
        }
        finally {
            if (this.#houseAdsAbortController === abortController) {
                this.#houseAdsAbortController = undefined;
            }
            debug('Outstream house ads playback loop ended', {
                isDestroyed: this.#isDestroyed,
                lifecycleVersion,
                currentLifecycleVersion: this.#lifecycleVersion,
            });
            this.#isPlayingHouseAds = false;
            houseAdsPlayer.stop();
            this.#hideHouseAdsMediaElement();
        }
    }
    #waitForHouseAdPlaybackResult(signal) {
        const houseAdsPlayer = this.#houseAdsPlayer;
        if (!houseAdsPlayer) {
            return Promise.resolve('aborted');
        }
        if (signal?.aborted) {
            return Promise.resolve('aborted');
        }
        const { promise, resolve } = Promise.withResolvers();
        let isSettled = false;
        const cleanup = () => {
            houseAdsPlayer.off('ended', onEnded);
            houseAdsPlayer.off('error', onError);
            houseAdsPlayer.off('stopped', onStopped);
            signal?.removeEventListener('abort', onAbort);
        };
        const settle = (result) => {
            if (isSettled) {
                return;
            }
            isSettled = true;
            cleanup();
            resolve(result);
        };
        const onAbort = () => settle('aborted');
        const onEnded = () => settle('ended');
        const onError = () => settle('error');
        const onStopped = () => settle(signal?.aborted ? 'aborted' : 'stopped');
        houseAdsPlayer.on('ended', onEnded);
        houseAdsPlayer.on('error', onError);
        houseAdsPlayer.on('stopped', onStopped);
        signal?.addEventListener('abort', onAbort, { once: true });
        return promise;
    }
    #reportLoopStall(elapsedMs, location) {
        api.logError(`Outstream ad loop stall detected at ${location}`, {
            elapsedMs,
            isDestroyed: this.#isDestroyed,
            lifecycleVersion: this.#lifecycleVersion,
            currentLifecycleVersion: this.#lifecycleVersion,
            isBlockedByMainPlayerVideoAd: this.#isBlockedByMainPlayerVideoAd,
            isPlayingHouseAds: this.#isPlayingHouseAds,
            pageIsVisible: pageLifeCycleWatcher.isVisible,
            hasPlayer: Boolean(this.#player),
            startPromiseExists: Boolean(this.#startPromise),
            activeAdRequestId: this.#activeAdRequestId,
            isInitializing: this.#isInitializing,
            houseAdsCount: this.#houseAdsSources.length,
        });
    }
    #reportLoopIterationError(error) {
        api.logError('Outstream ad loop iteration threw unexpectedly', {
            errorMessage: error?.message,
            errorStack: error?.stack,
            isDestroyed: this.#isDestroyed,
            lifecycleVersion: this.#lifecycleVersion,
            isBlockedByMainPlayerVideoAd: this.#isBlockedByMainPlayerVideoAd,
            isPlayingHouseAds: this.#isPlayingHouseAds,
            pageIsVisible: pageLifeCycleWatcher.isVisible,
            hasPlayer: Boolean(this.#player),
            startPromiseExists: Boolean(this.#startPromise),
            activeAdRequestId: this.#activeAdRequestId,
            isInitializing: this.#isInitializing,
            houseAdsCount: this.#houseAdsSources.length,
        });
    }
    async #waitForRetryDelay(retryDelaySeconds, lifecycleVersion) {
        debug('Entering outstream retry delay', {
            houseAdsCount: this.#houseAdsSources.length,
            lifecycleVersion,
            retryDelaySeconds,
        });
        this.#startHouseAdsLoop(lifecycleVersion);
        const loopSignal = this.#loopAbortController?.signal;
        await wait(retryDelaySeconds * 1000, loopSignal);
        if (loopSignal?.aborted || this.#isDestroyed || lifecycleVersion !== this.#lifecycleVersion) {
            debug('Outstream retry delay was interrupted before the next request', {
                isDestroyed: this.#isDestroyed,
                lifecycleVersion,
                currentLifecycleVersion: this.#lifecycleVersion,
            });
            return;
        }
        debug('Outstream retry delay elapsed; requesting the next outstream ad while house ads continue');
    }
    // ── Visibility ────────────────────────────────────────────────────────
    #waitForVisibility(signal) {
        if (pageLifeCycleWatcher.isVisible || signal?.aborted) {
            return Promise.resolve();
        }
        const { promise, resolve } = Promise.withResolvers();
        const cleanup = () => {
            pageLifeCycleWatcher.off('change', onChange);
            signal?.removeEventListener('abort', onAbort);
        };
        const onChange = () => {
            if (pageLifeCycleWatcher.isVisible) {
                cleanup();
                resolve();
            }
        };
        const onAbort = () => {
            cleanup();
            resolve();
        };
        pageLifeCycleWatcher.on('change', onChange);
        signal?.addEventListener('abort', onAbort, { once: true });
        return promise;
    }
    #waitForMainPlayerAdComplete(signal) {
        if (!this.#isBlockedByMainPlayerVideoAd || signal?.aborted) {
            return Promise.resolve();
        }
        const { promise, resolve } = Promise.withResolvers();
        const cleanup = () => {
            this.#mainPlayerBlockResolvers.delete(onResolved);
            signal?.removeEventListener('abort', onAbort);
        };
        const onResolved = () => {
            cleanup();
            resolve();
        };
        const onAbort = () => {
            cleanup();
            resolve();
        };
        this.#mainPlayerBlockResolvers.add(onResolved);
        signal?.addEventListener('abort', onAbort, { once: true });
        return promise;
    }
    #resolveMainPlayerBlockWaiters() {
        for (const resolve of [...this.#mainPlayerBlockResolvers]) {
            resolve();
        }
        this.#mainPlayerBlockResolvers.clear();
    }
    // ── Session & config ──────────────────────────────────────────────────
    #syncSessionConfig(resetState = false) {
        const previousGuideId = this.#sessionConfig?.guideId;
        const sessionConfig = this.#getSessionConfigFromPlayerManager();
        const nextGuideId = sessionConfig.guideId;
        this.#sessionConfig = sessionConfig;
        if (!nextGuideId) {
            debug('No guide id available from player manager; using page-level outstream context', {
                hasNowPlaying: Boolean(sessionConfig.nowPlaying),
            });
            return;
        }
        if (resetState || (previousGuideId && nextGuideId !== previousGuideId)) {
            debug('Outstream session config updated', {
                previousGuideId,
                guideId: nextGuideId,
                hasNowPlaying: Boolean(sessionConfig.nowPlaying),
            });
            this.#resetSessionState();
        }
    }
    #getSessionConfigFromPlayerManager() {
        return {
            guideId: this.#playerManager?.guideItem?.guideId,
            nowPlaying: this.#playerManager?.streamData?.nowPlaying,
        };
    }
    async #updateOutstreamVideoAdConfig() {
        if (!this.#containerId) {
            debug('Skipping outstream WMP config update because no container id was provided');
            return;
        }
        const requestTimeoutSeconds = api.getConfigSetting('ads.outstreamVideoAd.requestTimeoutSeconds', 60);
        const outstreamVideoAdConfig = {
            adWillPlayMuted: this.#muted,
            containerId: this.#containerId,
            mediaElementId: this.#mediaElementId,
            adLoaderTimeout: requestTimeoutSeconds * 1000,
        };
        debug('Updating outstream WMP config', outstreamVideoAdConfig);
        await appSessionDataProvider.update({ outstreamVideoAdConfig });
    }
    async #initializeHeaderBidder() {
        const isFreestarEnabled = api.getConfigSetting(FREESTAR_ENABLED, false);
        this.#headerBidder.on('bidRequested', this.#onBidsRequested);
        this.#headerBidder.on('bidResponseReceived', this.#onBidsResponseReceived);
        this.#headerBidder.on('bidRequestFailed', this.#onBidsRequestFailed);
        await this.#headerBidder.init({
            useApsIframe: isFreestarEnabled,
            consentData: appSessionDataProvider.consentData,
        });
        // Pre-warm the video bid cache so the first outstream request doesn't
        // block on the first fetch. Gated on presence of any configured partner
        // in the partner ads config.
        if (api.getPrebiddingConfig(clientAdTypes.outstream)) {
            this.#headerBidder.pollAndCacheVideoBids();
        }
    }
    #resetSessionState() {
        this.#didVideoAdPlay = false;
        this.#isFirstInSession = true;
    }
    #getSessionMetadata(adUnitId, provider, adRequestId, adRequestUrl, biddingNetworks) {
        const { primaryGuideId, secondaryGuideId } = (this.#sessionConfig?.nowPlaying ||
            {});
        return {
            adUnitId,
            adRequestId,
            adRequestUrl,
            guideIds: [primaryGuideId, secondaryGuideId].filter(Boolean).join(','),
            adNetworkName: providerToAdNetworkMap[provider],
            biddingNetworks,
        };
    }
    #getRequestContextUrl(guideId) {
        if (guideId) {
            return `https://tunein.com/desc/${guideId}/`;
        }
        return DEFAULT_OUTSTREAM_REQUEST_CONTEXT_URL;
    }
    // ── Ad tag ────────────────────────────────────────────────────────────
    #generateAdTagUrl(adUnitId, videoApsBids) {
        const { guideId, nowPlaying } = this.#sessionConfig || {};
        const { consentData } = appSessionDataProvider;
        const { isTargetingEnabled, gdprApplies, gdprTcString, usPrivacyString = '' } = consentData;
        const url = this.#getRequestContextUrl(guideId);
        const commonCustParams = buildCommonCustomParams({
            videoAdPlayed: this.#didVideoAdPlay,
            isFirstInSession: this.#isFirstInSession,
        });
        const usPrivacyParam = usPrivacyString && { us_privacy: usPrivacyString };
        const baseParams = {
            ad_rule: '1',
            ad_type: 'standardvideo',
            env: 'vp',
            output: 'vmap',
            trt: '2',
            url,
            iu: adUnitId,
            description_url: url,
            ppid: isTargetingEnabled ? api.adsTargeting?.ppid : undefined,
            sz: '1x1|640x360|640x480',
            vpmute: this.#muted ? '1' : '0',
            npa: isTargetingEnabled ? '0' : '1',
            gdpr: gdprApplies ? '1' : '0',
            pp: api.adsTargeting?.ads_partner_alias,
            ...usPrivacyParam,
            ...(guideId && { scor: guideId.slice(1) }),
            ...(gdprApplies && gdprTcString && { gdpr_consent: gdprTcString }),
            cust_params: {
                ...commonCustParams,
                ...nowPlaying?.adTargeting,
                partnerId: api.partnerId,
                ...usPrivacyParam,
            },
        };
        if (videoApsBids?.adParams && api.getConfigSetting(APS_BIDS_RESPONSE_LOGGING_ENABLED)) {
            api.telemetry.reportSandbox({
                category: 'aps_bids',
                action: 'outstream',
                props: {
                    bids: JSON.stringify(videoApsBids.adParams),
                },
            });
        }
        const adTagUrl = buildGoogleIMAAdTagUrl({
            baseParams,
            isVideoAd: true,
            videoApsBids,
        });
        debug('Built outstream ad request URL', {
            adUnitId,
            guideId,
            isFirstInSession: this.#isFirstInSession,
            didVideoAdPlay: this.#didVideoAdPlay,
            muted: this.#muted,
        });
        debug.json('Outstream ad request URL params', adTagUrl);
        return adTagUrl;
    }
    #reportPlaybackNotStarted(adRequestId, adUnitConfig) {
        const didLoadJavaScriptCreative = isJavaScriptAdEvent(this.#loadedAdEvent);
        const errorMessage = didLoadJavaScriptCreative
            ? 'Unsupported JavaScript/VPAID outstream creative loaded without playback start'
            : 'Outstream ad creative loaded but playback never started';
        const debugDescription = didLoadJavaScriptCreative
            ? 'Outstream Video Ad, Unsupported JavaScript Creative'
            : 'Outstream Video Ad, Playback Did Not Start';
        debug.warn('Outstream ad creative loaded but playback never started', {
            adPosition: this.#loadedAdEvent?.adPosition,
            adRequestId,
            adUnitId: adUnitConfig.adUnitId,
            contentType: this.#loadedAdEvent?.contentType,
            creativeId: this.#loadedAdEvent?.creativeId,
            didLoadJavaScriptCreative,
        });
        this.#reporter.reportPlaybackNotStarted(errorMessage, debugDescription);
    }
    // ── DOM ───────────────────────────────────────────────────────────────
    #watchSlotLayout(containerElement) {
        if (this.#slotLayoutObserver || !globalThis.MutationObserver) {
            return;
        }
        const slotLayoutObserver = new globalThis.MutationObserver(() => {
            this.#syncSlotMediaLayout();
        });
        slotLayoutObserver.observe(containerElement, {
            childList: true,
            subtree: true,
        });
        this.#slotLayoutObserver = slotLayoutObserver;
    }
    #disconnectSlotLayoutObserver() {
        this.#slotLayoutObserver?.disconnect();
        this.#slotLayoutObserver = undefined;
    }
    #watchSlotSize(containerElement) {
        if (this.#slotSizeObserver || !globalThis.ResizeObserver) {
            return;
        }
        const slotSizeObserver = new globalThis.ResizeObserver(() => {
            this.#syncSlotSize(containerElement);
        });
        slotSizeObserver.observe(containerElement);
        this.#slotSizeObserver = slotSizeObserver;
    }
    #disconnectSlotSizeObserver() {
        this.#slotSizeObserver?.disconnect();
        this.#slotSizeObserver = undefined;
    }
    #syncSlotMediaLayout() {
        const outstreamMediaElement = this.#player?.adMediaElement;
        if (outstreamMediaElement) {
            this.#applyMediaElementLayout(outstreamMediaElement, {
                pointerEvents: 'none',
            });
        }
        const houseAdsMediaElement = this.#getHouseAdsMediaElement();
        if (houseAdsMediaElement) {
            this.#applyMediaElementLayout(houseAdsMediaElement, {
                objectFit: 'cover',
                zIndex: '2',
            });
        }
    }
    #ensureSlotLayout() {
        const containerElement = document.getElementById(this.#containerId);
        if (!containerElement) {
            return;
        }
        const containerPosition = getComputedStyle(containerElement).position;
        if (!containerPosition || containerPosition === 'static') {
            containerElement.style.position = 'relative';
        }
        this.#watchSlotLayout(containerElement);
        this.#watchSlotSize(containerElement);
        this.#syncSlotMediaLayout();
        return containerElement;
    }
    #hideOutstreamContainer() {
        const containerElement = document.getElementById(this.#containerId);
        if (!containerElement) {
            return;
        }
        containerElement.style.visibility = 'hidden';
    }
    #showOutstreamContainer() {
        const containerElement = document.getElementById(this.#containerId);
        if (!containerElement) {
            return;
        }
        containerElement.style.visibility = '';
    }
    #syncSlotSize(containerElement, { logMissingHeight = false, reason = 'unknown' } = {}) {
        const width = containerElement.clientWidth;
        const height = containerElement.clientHeight;
        if (width > 0 && height > 0) {
            this.#resizeVideoAd(width, height);
            return {
                didResize: true,
                height,
                width,
            };
        }
        if (logMissingHeight && width > 0 && height === 0) {
            debug('Outstream slot has no measurable height; host CSS must provide slot size', {
                containerId: this.#containerId,
                reason,
                width,
            });
        }
        return {
            didResize: false,
            height,
            width,
        };
    }
    #applyMediaElementLayout(mediaElement, { objectFit, pointerEvents, zIndex, } = {}) {
        mediaElement.style.position = 'absolute';
        mediaElement.style.top = '0';
        mediaElement.style.left = '0';
        mediaElement.style.width = '100%';
        mediaElement.style.height = '100%';
        if (zIndex) {
            mediaElement.style.zIndex = zIndex;
        }
        if (objectFit) {
            mediaElement.style.objectFit = objectFit;
        }
        if (pointerEvents) {
            // Let the IMA overlay beneath the transport video receive click-through interactions.
            mediaElement.style.pointerEvents = pointerEvents;
        }
    }
    #applyVolume() {
        this.#player?.setVolume(this.#muted ? 0 : this.#volume);
        this.#houseAdsPlayer?.setVolume(this.#muted ? 0 : this.#volume);
        const houseAdsMediaElement = this.#getHouseAdsMediaElement();
        if (houseAdsMediaElement) {
            houseAdsMediaElement.muted = this.#muted;
            houseAdsMediaElement.volume = this.#muted ? 0 : this.#volume;
        }
        this.#controls?.updateMuteState(this.#muted);
        this.#controls?.updateVolume(this.#volume);
    }
    #resizeVideoAd(width, height) {
        debug.verbose('Resizing outstream ad container', { width, height });
        this.#player?.resizeVideoAd(width, height);
    }
    #isCurrentAdRequest(requestId) {
        if (!requestId || !this.#activeAdRequestId) {
            return true;
        }
        return requestId === this.#activeAdRequestId;
    }
    async #mountControls() {
        if (!this.#showControls) {
            return;
        }
        const containerElement = document.getElementById(this.#containerId);
        if (!containerElement) {
            debug('Skipping controls mount because the container element was not found');
            return;
        }
        const { OutstreamVideoAdControls } = await import('./OutstreamVideoAdControls');
        if (this.#isDestroyed) {
            return;
        }
        this.#controls = new OutstreamVideoAdControls(containerElement, this.#muted, this.#volume);
        this.#controls.on('mutetoggle', (muted) => this.mute(muted));
        this.#controls.on('volumechange', (volume) => this.setVolume(volume));
    }
    // ── Event wiring ──────────────────────────────────────────────────────
    #registerPlayerEvents() {
        if (!this.#player) {
            return;
        }
        debug.verbose('Registering outstream player events');
        forEach(this.#playerEvents, (callback, event) => {
            this.#player?.off(event, callback);
            this.#player?.on(event, callback);
        });
    }
    #deregisterPlayerEvents() {
        if (!this.#player) {
            return;
        }
        debug.verbose('Deregistering outstream player events');
        forEach(this.#playerEvents, (callback, event) => {
            this.#player?.off(event, callback);
        });
    }
    #registerMainPlayerEvents() {
        if (this.#mainPlayerEventsRegistered || !this.#playerManager) {
            return;
        }
        debug.verbose('Registering main-player ad coordination events');
        this.#playerManager.on('adSlotStarted', this.#onMainPlayerAdSlotStarted);
        this.#playerManager.on('adSlotEnded', this.#onMainPlayerAdSlotEnded);
        this.#mainPlayerEventsRegistered = true;
    }
    #deregisterMainPlayerEvents() {
        if (!this.#mainPlayerEventsRegistered) {
            this.#mainPlayerEventsRegistered = false;
            return;
        }
        debug.verbose('Deregistering main-player ad coordination events');
        this.#playerManager?.off('adSlotStarted', this.#onMainPlayerAdSlotStarted);
        this.#playerManager?.off('adSlotEnded', this.#onMainPlayerAdSlotEnded);
        this.#mainPlayerEventsRegistered = false;
    }
    // ── Event listeners ───────────────────────────────────────────────────
    #onAdLoaded = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adloaded event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug.verbose('Outstream ad loaded', event);
        this.#loadedAdEvent = event;
        const containerElement = this.#ensureSlotLayout();
        if (containerElement) {
            const { didResize, height, width } = this.#syncSlotSize(containerElement, {
                logMissingHeight: true,
                reason: 'adLoaded',
            });
            if (didResize) {
                debug.verbose('Synced outstream slot size', {
                    reason: 'adLoaded',
                    width,
                    height,
                });
            }
        }
        if (this.#isPlayingHouseAds ||
            this.#houseAdsPlayer?.isPlaying() ||
            this.#houseAdsPlayer?.isPaused()) {
            debug('Outstream ad loaded with fill; interrupting house ads for transition', {
                requestId: event.requestId,
            });
            this.#stopHouseAds({ reason: 'outstreamAdLoaded' });
        }
        if (isJavaScriptAdEvent(event)) {
            debug.warn('Outstream loaded a JavaScript/VPAID-style creative', {
                adPosition: event.adPosition,
                contentType: event.contentType,
                creativeId: event.creativeId,
                requestId: event.requestId,
            });
        }
        this.#reporter.reportAdLoaded(event);
    };
    #onAdPause = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adpause event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad paused', event);
        this.#reporter.reportAdPause(event);
    };
    #onAdClick = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adclick event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad clicked', event);
        this.#reporter.reportAdClick(event);
    };
    #onAdResume = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adresume event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad resumed', event);
        this.#reporter.reportAdResume(event);
    };
    #onAdStart = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adstart event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad started', event);
        this.#didCurrentAdStart = true;
        const containerElement = this.#ensureSlotLayout();
        if (containerElement) {
            const { didResize, height, width } = this.#syncSlotSize(containerElement, {
                logMissingHeight: true,
                reason: 'adStarted',
            });
            if (didResize) {
                debug.verbose('Synced outstream slot size', {
                    reason: 'adStarted',
                    width,
                    height,
                });
            }
        }
        if (this.#isPlayingHouseAds ||
            this.#houseAdsPlayer?.isPlaying() ||
            this.#houseAdsPlayer?.isPaused()) {
            debug('Outstream ad started before house ads fully stopped; forcing transition', {
                requestId: event.requestId,
            });
            this.#stopHouseAds({ reason: 'outstreamAdStarted' });
        }
        this.#reporter.reportAdStart(event);
        if (this.#isBlockedByMainPlayerVideoAd) {
            debug('Outstream ad started while the main player was in a video ad break; pausing it', {
                requestId: event.requestId,
            });
            this.pause();
        }
    };
    #onAdComplete = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adcomplete event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad completed', event);
        this.#reporter.reportAdComplete(event);
    };
    #onAdSkipped = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream adskipped event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        debug('Outstream ad skipped', event);
        this.#reporter.onAdSkipped(event);
    };
    #onAllAdsComplete = (event) => {
        if (!this.#isCurrentAdRequest(event.requestId)) {
            debug.verbose('Ignoring stale outstream alladscomplete event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: event.requestId,
            });
            return;
        }
        this.#didCurrentAdBreakComplete = true;
        debug('Outstream ad break exhausted', {
            adPosition: event.adPosition,
            requestId: event.requestId,
            totalAds: event.totalAds,
        });
        this.#reporter.reportAdBreakCompleted(event);
    };
    #onAdError = (error) => {
        if (!this.#isCurrentAdRequest(error.requestId)) {
            debug.verbose('Ignoring stale outstream aderror event', {
                activeAdRequestId: this.#activeAdRequestId,
                eventRequestId: error.requestId,
            });
            return;
        }
        debug.warn('Outstream ad error', error);
        this.#reporter.reportError(error);
    };
    #onPageVisibilityChange = () => {
        if (!pageLifeCycleWatcher.isVisible) {
            this.#hiddenAt = Date.now();
            debug('Page hidden; pausing outstream ad');
            this.pause();
            return;
        }
        const hiddenAt = this.#hiddenAt;
        this.#hiddenAt = undefined;
        if (!hiddenAt) {
            return;
        }
        const hiddenDurationMs = Date.now() - hiddenAt;
        const expirationMinutes = api.getConfigSetting('ads.outstreamVideoAd.expirationTimeoutMinutes', 60);
        const expirationMs = expirationMinutes * 60 * 1000;
        if (hiddenDurationMs > expirationMs) {
            debug('Outstream expired after being hidden; restarting', {
                hiddenDurationMs,
                expirationMinutes,
            });
            // Using sandbox to align with mobile client teams
            api.telemetry.reportSandbox({
                category: 'videoAds',
                action: 'ExpirationTimeout',
                props: {
                    hiddenDurationMs: hiddenDurationMs?.toString(),
                    expirationMinutes: expirationMinutes?.toString(),
                },
            });
            this.#restart();
        }
        else {
            if (this.#isPlayingHouseAds && this.#houseAdsPlayer?.isPaused()) {
                debug('Page visible; resuming outstream house ad', { hiddenDurationMs });
                this.#applyVolume();
                this.#houseAdsPlayer.play();
                return;
            }
            debug('Page visible; resuming outstream ad', { hiddenDurationMs });
            this.play();
        }
    };
    #onMainPlayerAdSlotStarted = (event) => {
        if (event.medium !== 'video') {
            debug.verbose('Ignoring non-video main-player ad slot start', event);
            return;
        }
        debug('Main-player video ad slot started; pausing outstream ad', event);
        this.#isBlockedByMainPlayerVideoAd = true;
        if (this.#houseAdsPlayer?.isPlaying() || this.#houseAdsPlayer?.isPaused()) {
            this.#stopHouseAds({ abortLoop: false, reason: 'mainPlayerVideoAdStarted' });
        }
        this.#hideOutstreamContainer();
        this.pause();
    };
    #onMainPlayerAdSlotEnded = (event) => {
        if (event.medium !== 'video') {
            debug.verbose('Ignoring non-video main-player ad slot end', event);
            return;
        }
        if (this.#isBlockedByMainPlayerVideoAd) {
            debug('Main-player video ad slot ended; clearing block', event);
        }
        this.#isBlockedByMainPlayerVideoAd = false;
        this.#showOutstreamContainer();
        this.#resolveMainPlayerBlockWaiters();
        if (pageLifeCycleWatcher.isVisible) {
            this.play();
        }
    };
    #onBidsRequested = (event) => {
        this.#reporter.reportAdsBiddingRequested(event);
    };
    #onBidsResponseReceived = (event) => {
        if (event.hasBid) {
            this.#reporter.reportAdsBiddingResponseReceived(event);
            return;
        }
        this.#reporter.reportAdsBiddingRequestFailed(event, 'singleBid');
    };
    #onBidsRequestFailed = (event) => {
        this.#reporter.reportAdsBiddingRequestFailed(event, 'allBids');
    };
}
